import { useEffect, useState } from 'react';
import * as React from 'react';
import {
  Alert,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Snackbar,
  SnackbarCloseReason,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import UndoRoundedIcon from '@mui/icons-material/UndoRounded';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';

dayjs.locale('pt-br');

import { FerramentasDeDetalhe } from '../../shared/components';
import { LayoutBaseDePagina } from '../../shared/layouts';
import { DespesaService, IDespesa } from '../../shared/services/api/controle/DespesaService';

const formatarMoeda = (valor: number) => valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

export const DetalheDespesaRecorrente: React.FC = () => {
  const { idrecorrencia = '' } = useParams<'idrecorrencia'>();
  const navigate = useNavigate();

  const [parcelas, setParcelas] = useState<IDespesa[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const [openmsg, setOpenmsg] = useState(false);
  const [msg, setMsg] = useState('');
  const [tms, setTms] = useState<'warning' | 'error' | 'info' | 'success'>('warning');

  const [dialogBaixa, setDialogBaixa] = useState<IDespesa | null>(null);
  const [dataPagamento, setDataPagamento] = useState(() => dayjs().format('YYYY-MM-DD'));
  const [formaPagamento, setFormaPagamento] = useState('Pix');
  const [isBaixando, setIsBaixando] = useState(false);

  const handleCloseMsg = (event?: React.SyntheticEvent | Event, reason?: SnackbarCloseReason) => {
    if (reason === 'clickaway') return;
    setOpenmsg(false);
  };

  const carregar = () => {
    setIsLoading(true);
    DespesaService.getParcelas(idrecorrencia).then((result) => {
      setIsLoading(false);
      if (result instanceof Error) {
        setTms('error');
        setMsg(result.message);
        setOpenmsg(true);
        return;
      }
      setParcelas(result);
    });
  };

  useEffect(() => { carregar(); }, [idrecorrencia]);

  const handleAbrirBaixa = (parcela: IDespesa) => {
    setDataPagamento(dayjs().format('YYYY-MM-DD'));
    setFormaPagamento('Pix');
    setDialogBaixa(parcela);
  };

  const handleConfirmarBaixa = () => {
    if (!dialogBaixa) return;
    setIsBaixando(true);
    DespesaService.baixa(dialogBaixa.iddespesa, dataPagamento, formaPagamento).then((result) => {
      setIsBaixando(false);
      if (result instanceof Error) {
        setTms('error');
        setMsg(result.message);
        setOpenmsg(true);
        return;
      }
      setDialogBaixa(null);
      carregar();
    });
  };

  const handleEstornar = (parcela: IDespesa) => {
    if (!window.confirm('Estornar a baixa dessa parcela? Ela volta a ficar pendente.')) return;
    DespesaService.estornarBaixa(parcela.iddespesa).then((result) => {
      if (result instanceof Error) {
        setTms('error');
        setMsg(result.message);
        setOpenmsg(true);
        return;
      }
      carregar();
    });
  };

  const handleApagar = (parcela: IDespesa) => {
    if (!window.confirm(`Apagar essa parcela (${dayjs(parcela.data).format('DD/MM/YYYY')})?`)) return;
    DespesaService.deleteById(parcela.iddespesa).then((result) => {
      if (result instanceof Error) {
        setTms('error');
        setMsg(result.message);
        setOpenmsg(true);
        return;
      }
      carregar();
    });
  };

  const descricao = parcelas[0]?.descricao || 'Lançamento recorrente';
  const categoria = parcelas[0]?.categoria;
  const totalPago = parcelas.filter((p) => p.status === 'pago').length;

  return (
    <>
      <LayoutBaseDePagina
        titulo={descricao}
        subtitulo={`${categoria ? `${categoria} • ` : ''}${totalPago}/${parcelas.length} parcelas pagas`}
        barraDeFerramentas={
          <FerramentasDeDetalhe
            mostrarBotaoNovo={false}
            mostrarBotaoApagar={false}
            mostrarBotaoImpressao={false}
            aoClicarEmVoltar={() => navigate('/despesas')}
            mostrarBotaoSalvar={false}
          />
        }
      >
        <Snackbar
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          open={openmsg}
          autoHideDuration={tms === 'error' ? null : 6000}
          onClose={handleCloseMsg}
        >
          <Alert onClose={handleCloseMsg} severity={tms} variant='filled' sx={{ width: '100%' }}>
            {msg}
          </Alert>
        </Snackbar>

        <Paper elevation={0} sx={{ p: 3, borderRadius: '12px' }}>
          <TableContainer>
            <Table size='small'>
              <TableHead>
                <TableRow>
                  <TableCell>Data</TableCell>
                  <TableCell align='right'>Valor</TableCell>
                  <TableCell align='center'>Status</TableCell>
                  <TableCell align='center'>Pagamento</TableCell>
                  <TableCell align='right'>Ação</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {parcelas.map((p) => (
                  <TableRow key={p.iddespesa}>
                    <TableCell>{dayjs(p.data).format('DD/MM/YYYY')}</TableCell>
                    <TableCell align='right'>{formatarMoeda(p.valor)}</TableCell>
                    <TableCell align='center'>
                      <Chip
                        label={p.status === 'pago' ? 'Pago' : 'Pendente'}
                        size='small'
                        color={p.status === 'pago' ? 'success' : 'warning'}
                      />
                    </TableCell>
                    <TableCell align='center'>{p.datapagamento ? dayjs(p.datapagamento).format('DD/MM/YYYY') : '-'}</TableCell>
                    <TableCell align='right'>
                      {p.status === 'pendente' ? (
                        <Button size='small' onClick={() => handleAbrirBaixa(p)}>Dar baixa</Button>
                      ) : (
                        <IconButton size='small' onClick={() => handleEstornar(p)} title='Estornar baixa'>
                          <UndoRoundedIcon fontSize='small' />
                        </IconButton>
                      )}
                      <IconButton size='small' onClick={() => handleApagar(p)} title='Apagar parcela'>
                        <DeleteOutlineRoundedIcon fontSize='small' />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {!isLoading && parcelas.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5} align='center'>
                      <Typography color='text.secondary' fontSize='0.85rem' sx={{ py: 2 }}>
                        Nenhuma parcela encontrada.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </LayoutBaseDePagina>

      <Dialog open={!!dialogBaixa} onClose={() => setDialogBaixa(null)} maxWidth='xs' fullWidth>
        <DialogTitle>Dar baixa na parcela</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                type='date'
                label='Data do pagamento'
                InputLabelProps={{ shrink: true }}
                value={dataPagamento}
                onChange={(e) => setDataPagamento(e.target.value)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth select label='Forma de pagamento' value={formaPagamento} onChange={(e) => setFormaPagamento(e.target.value)}>
                <MenuItem value='Pix'>Pix</MenuItem>
                <MenuItem value='Boleto'>Boleto</MenuItem>
                <MenuItem value='Cartão'>Cartão</MenuItem>
                <MenuItem value='Transferência'>Transferência</MenuItem>
                <MenuItem value='Dinheiro'>Dinheiro</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogBaixa(null)}>Cancelar</Button>
          <Button variant='contained' disabled={isBaixando} onClick={handleConfirmarBaixa}>Confirmar pagamento</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
